import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ticketService } from '../services/ticketService';
import { companyService, Company } from '../services/companyService';
import { TicketCategory, UserRole } from '../types';
import { useAuth } from '../context/AuthContext';

const CreateTicket: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: TicketCategory.GENERAL_INQUIRY
  });
  const [clientCompany, setClientCompany] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const categoryOptions = Object.values(TicketCategory).map(category => ({
    label: category.replace(/_/g, ' '),
    value: category
  }));
  const companyOptions = companies
    .filter((company) => company.type === 'CLIENT' && company.status !== 'INACTIVE')
    .map((company) => ({ label: company.name, value: company._id }));
  const assignedCompanyName = user?.company?.name || '';

  useEffect(() => {
    if (!isSuperAdmin) return;
    companyService.getCompanies()
      .then((list) => setCompanies(list || []))
      .catch(() => setError('Could not load companies'));
  }, [isSuperAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isSuperAdmin && !clientCompany) {
      setError('Select a company for this ticket');
      return;
    }

    setLoading(true);

    try {
      const ticket = await ticketService.createTicket({
        ...formData,
        ...(isSuperAdmin ? { clientCompany } : {})
      });
      navigate(`/tickets/${ticket._id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-ticket-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <i className="pi pi-ticket"></i>
          </div>
          <div>
            <h1 className="page-title">Create New Ticket</h1>
            <p className="page-subtitle">Fill out the form below to create a new ticket</p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <Card className="form-card">
        {error && (
          <Message 
            severity="error" 
            text={error} 
            className="error-message"
            style={{ marginBottom: '1.5rem', borderRadius: '12px' }}
          />
        )}

        <form onSubmit={handleSubmit}>
          {isSuperAdmin ? (
            <div className="form-field">
              <label htmlFor="company" className="field-label">
                <i className="pi pi-building"></i>
                Company <span className="required">*</span>
              </label>
              <Dropdown
                id="company"
                value={clientCompany}
                options={companyOptions}
                onChange={(e) => setClientCompany(e.value)}
                placeholder="Select a company"
                filter
                className="field-dropdown"
              />
              <small className="field-hint">The ticket will belong to this company</small>
            </div>
          ) : (
            assignedCompanyName && (
              <div className="form-field">
                <label htmlFor="assigned-company" className="field-label">
                  <i className="pi pi-building"></i>
                  Company
                </label>
                <InputText
                  id="assigned-company"
                  value={assignedCompanyName}
                  className="field-input"
                  disabled
                />
                <small className="field-hint">Tickets are created for your assigned company</small>
              </div>
            )
          )}
          {/* Title Field */}
          <div className="form-field">
            <label htmlFor="title" className="field-label">
              <i className="pi pi-pencil"></i>
              Title <span className="required">*</span>
            </label>
            <InputText
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Briefly describe the problem..."
              className="field-input"
              required
            />
            <small className="field-hint">Short and clear description of the problem</small>
          </div>

          {/* Description Field */}
          <div className="form-field">
            <label htmlFor="description" className="field-label">
              <i className="pi pi-align-left"></i>
              Description <span className="required">*</span>
            </label>
            <InputTextarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the problem in detail..."
              rows={6}
              className="field-input field-textarea"
              required
            />
            <small className="field-hint">Include as many details as possible so we can solve the problem faster</small>
          </div>

          {/* Category Field */}
          <div className="form-field">
            <label htmlFor="category" className="field-label">
              <i className="pi pi-tag"></i>
              Category
            </label>
            <Dropdown
              id="category"
              value={formData.category}
              options={categoryOptions}
              onChange={(e) => setFormData({ ...formData, category: e.value })}
              placeholder="Select a category"
              className="field-dropdown"
            />
            <small className="field-hint">Type of problem</small>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <Button
              label="Cancel"
              icon="pi pi-times"
              type="button"
              className="cancel-btn"
              onClick={() => navigate('/tickets')}
            />
            <Button
              label="Create Ticket"
              icon="pi pi-check"
              type="submit"
              className="submit-btn"
              loading={loading}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none'
              }}
            />
          </div>
        </form>
      </Card>

      <style>{`
        .create-ticket-container {
          max-width: 900px;
          margin: 0 auto;
          animation: fadeIn 0.4s ease;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
          flex-shrink: 0;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0.5rem 0 0 0;
        }

        .form-card {
          border-radius: 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(16, 185, 129, 0.1);
        }

        .form-card .p-card-body {
          padding: 2.5rem;
        }

        .error-message {
          width: 100%;
          margin-bottom: 1.5rem;
        }

        .form-field {
          margin-bottom: 2rem;
        }

        .field-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          color: #374151;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .field-label i {
          color: #10b981;
          font-size: 1rem;
        }

        .required {
          color: #ef4444;
          font-weight: 700;
        }

        .field-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border-radius: 12px;
          border: 2px solid #e5e7eb;
          transition: all 0.2s ease;
          font-size: 0.95rem;
          font-family: 'Inter', sans-serif;
        }

        .field-input:hover {
          border-color: #10b981;
        }

        .field-input:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
          outline: none;
        }

        .field-textarea {
          resize: vertical;
          min-height: 150px;
          line-height: 1.6;
        }

        .field-hint {
          display: block;
          margin-top: 0.5rem;
          color: #9ca3af;
          font-size: 0.875rem;
          font-style: italic;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .field-dropdown .p-dropdown {
          width: 100%;
          border-radius: 12px;
          border: 2px solid #e5e7eb;
          transition: all 0.2s ease;
          padding: 0.5rem;
        }

        .field-dropdown .p-dropdown:hover {
          border-color: #10b981;
        }

        .field-dropdown .p-dropdown:not(.p-disabled).p-focus {
          border-color: #10b981;
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 2.5rem;
          padding-top: 2rem;
          border-top: 2px solid #f3f4f6;
        }

        .cancel-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          background: #f3f4f6;
          border: 2px solid #e5e7eb;
          color: #374151;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .cancel-btn:hover {
          background: #e5e7eb;
          border-color: #d1d5db;
          color: #111827;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .submit-btn {
          padding: 0.75rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          transition: all 0.2s ease;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
        }

        .submit-btn:active {
          transform: translateY(0);
        }

        @media (max-width: 768px) {
          .create-ticket-container {
            padding: 0;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .header-icon {
            width: 56px;
            height: 56px;
            font-size: 24px;
          }

          .page-title {
            font-size: 1.5rem;
          }

          .form-card .p-card-body {
            padding: 1.5rem;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .cancel-btn,
          .submit-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CreateTicket; 